import { DefaultPalette } from '@uifabric/experiments/lib/Styling';
import moment from 'moment';
import * as fabric from 'office-ui-fabric-react';
import { IStackItemStyles, IStackStyles, IStackTokens, Stack } from 'office-ui-fabric-react';
import React, { FC, useEffect, useState } from 'react';
import * as api from '../../../api/generated';
import * as components from '../../../components';
import { KendoLoading } from '../../../components';
import { ShowErrorModal } from '../../queueManagement/components/ShowErrorModal';
import { INugisStatisticFilter } from './NugisStatistics';

interface IProps {
    timestamp: string | undefined;
    filter: INugisStatisticFilter;
}

export interface IStatisticSummary {
    patientMinWaitTime: string | null;
    patientAvgWaitTime: string | null;
    patientMaxWaitTime: string | null;
    serviceMinTime: string | null;
    serviceAvgTime: string | null;
    serviceMaxTime: string | null;

    waitingPatientsCount: number | null;
    invitedPatientsCount: number | null;
    servicedPatientsCount: number | null;
    servedPatientsCount: number | null;
    servedInvitedPatientsCount: number | null;
    servedNotInvitedPatientsCount: number | null;
    stoppedPatientsCount: number | null;
    removedPatientsCount: number | null;
}

const stackItemStylesBorder: IStackItemStyles = {
    root: {
        borderStyle: 'solid',
        borderWidth: '2px',
        borderColor: 'grey',
        color: DefaultPalette.black,
        padding: 5,
        textAlign: 'center'
    }
};

const stackItemStyles: IStackItemStyles = {
    root: {
        color: DefaultPalette.black,
        padding: 5,
        textAlign: 'center'
    }
};
const itemAlignmentsStackStyles: IStackStyles = {
    root: {
        verticalAlign: 'center',
        boxShadow: '0px 0px 5px 2px grey',
        height: 100
    }
};

const itemAlignmentsStackTokens: IStackTokens = {
    childrenGap: 5,
    padding: 20
};

const styles = fabric.mergeStyleSets({
    wrapperClass: {
        padding: 2,
        selectors: {
            '& > .ms-Shimmer-container': {
                margin: '10px 0'
            }
        }
    },
    fontL: {
        fontSize: '1vw'
    },
    fontM: {
        fontSize: '0.8vw'
    },
    itemAlignmentsStackStyles: {
        padding: 20,
        height: 65,
        verticalAlign: 'center',
        boxShadow: '0px 0px 5px 2px grey'
    }
});

const NugisStatisticsSumary: FC<IProps> = props => {
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<IStatisticSummary | null | undefined>(null);

    const fetchData = async () => {
        try {
            setSummary(null);
            const resp = await api.statistic.getNugisStatisticsSummary({
                ...props.filter,
                practitioners: props.filter.practitioners.map(x => x.text),
                dateFrom: moment(props.filter.dateFrom!),
                dateTo: moment(props.filter.dateTo!),
                timeFrom: props.filter.timeFrom!,
                timeTo: props.filter.timeTo!
            });
            setSummary(resp);
        } catch (err) {
            setError(err.error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [props.timestamp]);

    return (
        <>
            {summary && (
                <div className="ms-Grid" dir="ltr">
                    <div className="ms-Grid-row" style={{ display: 'flex', justifyContent: 'center' }}>
                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                            <Stack horizontal horizontalAlign="space-evenly" styles={itemAlignmentsStackStyles} tokens={itemAlignmentsStackTokens}>
                                <Stack.Item align="center">
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Min</span>
                                    </div>
                                    <Stack.Item styles={stackItemStylesBorder}>
                                        <span className={styles.fontL}>{summary?.patientMinWaitTime}</span>
                                    </Stack.Item>
                                </Stack.Item>

                                <Stack.Item align="center">
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Paciento vid. laukimo laikas</span>
                                    </div>
                                    <Stack.Item styles={stackItemStylesBorder}>
                                        <span className={styles.fontL}>{summary?.patientAvgWaitTime}</span>
                                    </Stack.Item>
                                </Stack.Item>

                                <Stack.Item align="center">
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Max</span>
                                    </div>
                                    <Stack.Item styles={stackItemStylesBorder}>
                                        <span className={styles.fontL}>{summary?.patientMaxWaitTime}</span>
                                    </Stack.Item>
                                </Stack.Item>
                            </Stack>
                        </div>
                        <div className="ms-Grid-col ms-sm4 ms-md4 ms-lg4">
                            <Stack horizontal horizontalAlign="space-evenly" styles={itemAlignmentsStackStyles} tokens={itemAlignmentsStackTokens}>
                                <Stack.Item align="center">
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Min</span>
                                    </div>
                                    <Stack.Item styles={stackItemStylesBorder}>
                                        <span className={styles.fontL}>{summary?.serviceMinTime}</span>
                                    </Stack.Item>
                                </Stack.Item>

                                <Stack.Item align="center">
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Vidutinis aptarnavimo laikas</span>
                                    </div>
                                    <Stack.Item styles={stackItemStylesBorder}>
                                        <span className={styles.fontL}>{summary?.serviceAvgTime}</span>
                                    </Stack.Item>
                                </Stack.Item>

                                <Stack.Item align="center">
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Max</span>
                                    </div>
                                    <Stack.Item styles={stackItemStylesBorder}>
                                        <span className={styles.fontL}>{summary?.serviceMaxTime}</span>
                                    </Stack.Item>
                                </Stack.Item>
                            </Stack>
                        </div>
                    </div>
                    <div className="ms-Grid-row">&nbsp;</div>
                    <div className="ms-Grid-row" style={{ display: 'flex', justifyContent: 'center' }}>
                        <div className="ms-Grid-col">
                            <div className={styles.itemAlignmentsStackStyles}>
                                <div style={{ textAlign: 'center' }}>
                                    <span className={styles.fontM}>Atvykę</span>
                                </div>
                                <Stack.Item styles={stackItemStyles}>
                                    <span className={styles.fontL}>{summary?.waitingPatientsCount}</span>
                                </Stack.Item>
                            </div>
                        </div>
                        <div className="ms-Grid-col">
                            <div className={styles.itemAlignmentsStackStyles}>
                                <div style={{ textAlign: 'center' }}>
                                    <span className={styles.fontM}>Pakviesti</span>
                                </div>
                                <Stack.Item styles={stackItemStyles}>
                                    <span className={styles.fontL}>{summary?.invitedPatientsCount}</span>
                                </Stack.Item>
                            </div>
                        </div>
                        <div className="ms-Grid-col">
                            <div className={styles.itemAlignmentsStackStyles}>
                                <div style={{ textAlign: 'center' }}>
                                    <span className={styles.fontM}>Aptarnaujami</span>
                                </div>
                                <Stack.Item styles={stackItemStyles}>
                                    <span className={styles.fontL}>{summary?.servicedPatientsCount}</span>
                                </Stack.Item>
                            </div>
                        </div>
                        <div className="ms-Grid-col">
                            <div className={styles.itemAlignmentsStackStyles}>
                                <div style={{ textAlign: 'center' }}>
                                    <span className={styles.fontM}>Aptarnauti</span>
                                </div>
                                <Stack.Item styles={stackItemStyles}>
                                    <span className={styles.fontL}>{summary?.servedPatientsCount}</span>
                                </Stack.Item>
                            </div>
                        </div>
                        {summary?.servedPatientsCount != summary?.servedInvitedPatientsCount && (
                            <div className="ms-Grid-col">
                                <div className={styles.itemAlignmentsStackStyles}>
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Aptarnauti pakvietus</span>
                                    </div>
                                    <Stack.Item styles={stackItemStyles}>
                                        <span className={styles.fontL}>{summary?.servedInvitedPatientsCount}</span>
                                    </Stack.Item>
                                </div>
                            </div>
                        )}
                        {summary?.servedPatientsCount != summary?.servedInvitedPatientsCount && (
                            <div className="ms-Grid-col">
                                <div className={styles.itemAlignmentsStackStyles}>
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={styles.fontM}>Aptarnauti nepakvietus</span>
                                    </div>
                                    <Stack.Item styles={stackItemStyles}>
                                        <span className={styles.fontL}>{summary?.servedNotInvitedPatientsCount}</span>
                                    </Stack.Item>
                                </div>
                            </div>
                        )}
                        <div className="ms-Grid-col">
                            <div className={styles.itemAlignmentsStackStyles}>
                                <div style={{ textAlign: 'center' }}>
                                    <span className={styles.fontM}>Sustabdyti</span>
                                </div>
                                <Stack.Item styles={stackItemStyles}>
                                    <span className={styles.fontL}>{summary?.stoppedPatientsCount}</span>
                                </Stack.Item>
                            </div>
                        </div>
                        <div className="ms-Grid-col">
                            <div className={styles.itemAlignmentsStackStyles}>
                                <div style={{ textAlign: 'center' }}>
                                    <span className={styles.fontM}>Neatvykę, anuliuoti</span>
                                </div>
                                <Stack.Item styles={stackItemStyles}>
                                    <span className={styles.fontL}>{summary?.removedPatientsCount}</span>
                                </Stack.Item>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {(summary == null || summary == undefined) && <KendoLoading />}
            {error && <ShowErrorModal logout={true} error={error}></ShowErrorModal>}
        </>
    );
};

export default NugisStatisticsSumary;
